/**
 * <ReturnsPanel open onClose scope compareLabel onOpenSku />  - what the Sales overview's Returns card opens.
 *
 * GET /api/sales-hub/returns for the page scope (same period, comparison and filters; same total as the card).
 * Sections: headline rates (all returns, customer returns, not delivered, estimated value, deducted), the cohort rate
 * (of this period's API-order sales, how many came back so far), why (reason buckets), what came back (condition),
 * where (platform / brand / category), problem SKUs (by units, or by rate against the category), and what is not
 * counted yet. A SKU opens the product drawer (onOpenSku). Data notes at the bottom.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '../ui/sheet';
import { Skeleton } from '../ui/skeleton';
import { LoadError } from '../LoadError';
import { useSalesData } from './salesApi';
import { ShareBar } from './ShareBar';
import { MetricToggle } from './views/common';
import { number, shortMoney } from './format';

const pct = value => (value == null ? '—' : `${Number(value).toFixed(1)}%`);
const BUCKET_COLOR = { quality: '#DC2626', listing: '#D97706', mind: '#64748B', delivery: '#0EA5E9', damage: '#7C3AED', switch: '#BE185D', other: '#94A3B8' };
const CONDITION_COLOR = { recoverable: '#16A34A', claim: '#7C3AED', loss: '#DC2626', unknown: '#94A3B8' };
const WHERE = [['by_platform', 'Platforms'], ['by_brand', 'Brands'], ['by_category', 'Categories']];
const SKU_VIEWS = [['by_units', 'Most returned'], ['by_rate', 'Highest rate']];

// " · +1.2 pts" against the comparison period (nothing when there is no comparison). Lower is better.
const Change = ({ now, before, invert = true, points = false }) => {
  if (now == null || before == null) return null;
  const delta = points ? Math.round((now - before) * 10) / 10 : Math.round(now - before);   // at the precision shown
  if (!delta) return <> · <span className="text-gray-500">no change</span></>;
  const good = invert ? delta < 0 : delta > 0;
  const text = points ? `${delta > 0 ? '+' : '−'}${Math.abs(delta).toFixed(1)} pts` : `${delta > 0 ? '+' : '−'}${number(Math.abs(delta))}`;
  return <> · <span className={good ? 'text-green-700' : 'text-red-700'}>{text}</span></>;
};

const Stat = ({ label, value, sub, testId }) => (
  <div className="min-w-0 rounded-lg border border-gray-200 bg-white p-3" data-testid={testId}>
    <div className="text-[11px] font-medium uppercase tracking-wide text-gray-500">{label}</div>
    <div className="mt-0.5 text-lg font-semibold tabular-nums text-gray-900">{value}</div>
    {sub && <div className="mt-0.5 text-[11px] text-gray-600">{sub}</div>}
  </div>
);

const Section = ({ title, subtitle, right, children, testId }) => (
  <section className="space-y-2" data-testid={testId}>
    <div className="flex flex-wrap items-end justify-between gap-2">
      <div>
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
      </div>
      {right}
    </div>
    {children}
  </section>
);

const Breakdown = ({ data, colors, testId, itemsLabel }) => {
  const [open, setOpen] = useState(false);
  if (!data?.groups?.length) return <p className="text-xs text-gray-500">No returns in this period.</p>;
  return (
    <div className="space-y-2" data-testid={testId}>
      {data.groups.map(g => (
        <ShareBar key={g.group} label={g.label} value={`${number(g.units)} units`} share={g.share} color={colors[g.group] || '#94A3B8'}
          sublabel={g.previous_units != null ? `${number(g.previous_units)} in the comparison period` : null} />
      ))}
      <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open}
        className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
        {open ? <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" /> : <ChevronRight className="h-3.5 w-3.5" aria-hidden="true" />}
        {open ? 'Hide' : 'Show'} each {itemsLabel}
      </button>
      {open && (
        <table className="w-full text-xs">
          <thead className="text-left text-gray-500"><tr><th className="py-1 font-medium">{itemsLabel[0].toUpperCase() + itemsLabel.slice(1)}</th><th className="py-1 text-right font-medium">Units</th><th className="py-1 text-right font-medium">Share</th><th className="py-1 text-right font-medium">Value (est.)</th></tr></thead>
          <tbody>
            {data.items.map(i => (
              <tr key={i.code} className="border-t border-gray-100">
                <td className="py-1"><span className="mr-1.5 inline-block h-2 w-2 rounded-full" style={{ background: colors[i.group] || '#94A3B8' }} aria-hidden="true" />{i.label}</td>
                <td className="py-1 text-right tabular-nums">{number(i.units)}</td>
                <td className="py-1 text-right tabular-nums">{pct(i.share)}</td>
                <td className="py-1 text-right tabular-nums">{shortMoney(i.value_est)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

const WhereTable = ({ rows }) => {
  if (!rows?.length) return <p className="text-xs text-gray-500">No returns in this period.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[480px] text-xs" data-testid="returns-where-table">
        <thead className="bg-gray-50 text-left text-[11px] text-gray-600">
          <tr>
            <th className="px-2 py-1.5 font-medium">Name</th>
            <th className="px-2 py-1.5 text-right font-medium">Sold</th>
            <th className="px-2 py-1.5 text-right font-medium">Customer returns</th>
            <th className="px-2 py-1.5 text-right font-medium">Rate</th>
            <th className="px-2 py-1.5 text-right font-medium">Not delivered</th>
            <th className="px-2 py-1.5 text-right font-medium">Value (est.)</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(r => (
            <tr key={r.name} className="border-t border-gray-100">
              <td className="px-2 py-1.5 font-medium text-gray-900">{r.label || r.name}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{number(r.sold_units)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{number(r.customer_units)}</td>
              <td className={`px-2 py-1.5 text-right tabular-nums ${r.customer_rate >= 10 ? 'font-semibold text-red-700' : ''}`}>{pct(r.customer_rate)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums text-gray-600">{number(r.undelivered_units)}</td>
              <td className="px-2 py-1.5 text-right tabular-nums">{shortMoney(r.value_est)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ProblemSkus = ({ rows, onOpenSku, view }) => {
  if (!rows?.length) {
    return <p className="text-xs text-gray-500">{view === 'by_rate' ? 'No SKU has enough sales and customer returns to rank by rate.' : 'No customer returns in this period.'}</p>;
  }
  return (
    <ul className="divide-y divide-gray-100" data-testid={`returns-problem-${view}`}>
      {rows.map(r => (
        <li key={r.sku_code} className="py-2 text-xs">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <button type="button" onClick={() => onOpenSku?.(r.sku_code)} className="min-w-0 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
              <span className="font-mono font-semibold text-blue-700 hover:underline">{r.sku_code}</span>
              <span className="ml-2 text-gray-700">{r.product_name}</span>
            </button>
            <span className="tabular-nums">
              <span className={`font-semibold ${r.outlier ? 'text-red-700' : 'text-gray-900'}`}>{pct(r.customer_rate)}</span>
              <span className="text-gray-500"> · {number(r.customer_units)} of {number(r.sold_units)} sold</span>
            </span>
          </div>
          <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-gray-600">
            <span>{r.category}{r.category_customer_rate != null ? ` (category ${pct(r.category_customer_rate)})` : ''}</span>
            {r.top_reason && <span>Mostly: {r.top_reason}</span>}
            {r.top_condition && <span>Came back: {r.top_condition}</span>}
            {r.undelivered_units > 0 && <span>+{number(r.undelivered_units)} not delivered</span>}
            {r.outlier && <span className="rounded-full bg-red-50 px-1.5 font-medium text-red-800 ring-1 ring-inset ring-red-200">Well above its category</span>}
          </div>
        </li>
      ))}
    </ul>
  );
};

export const ReturnsPanel = ({ open, onClose, scope, compareLabel, onOpenSku }) => (
  <Sheet open={open} onOpenChange={value => { if (!value) onClose(); }}>
    <SheetContent side="right" className="w-full overflow-y-auto sm:max-w-2xl" data-testid="returns-panel">
      {/* Mounted only while open: reopening never shows an older period's figures */}
      {open && <ReturnsBody scope={scope} compareLabel={compareLabel} onOpenSku={onOpenSku} />}
    </SheetContent>
  </Sheet>
);

const ReturnsBody = ({ scope, compareLabel, onOpenSku }) => {
  const { data, loading, error, refresh } = useSalesData('returns', scope);
  const [where, setWhere] = useState('by_category');
  const [skuView, setSkuView] = useState('by_units');
  const s = data?.summary || {};
  const c = data?.cohort;
  const nc = data?.not_counted;
  return (
    <>
        <SheetHeader>
          <SheetTitle>Returns</SheetTitle>
          <SheetDescription>{data ? `${data.period.date_from} to ${data.period.date_to} · ${compareLabel || 'compared with the previous period'}` : 'Same period and filters as the overview'}</SheetDescription>
        </SheetHeader>
        {error && (
          <div className="mt-4">
            <LoadError what={data ? 'the returns for these filters (older figures shown below)' : 'returns'} error={error} onRetry={refresh} retrying={loading} testId="returns-panel-error" />
          </div>
        )}
        {!data && !error && (
          <div className="mt-4 space-y-3" aria-busy="true" aria-label="Loading returns">
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">{[0, 1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-20" />)}</div>
            <Skeleton className="h-40" />
          </div>
        )}
        {data && (
          <div className={`mt-4 space-y-6 ${loading ? 'opacity-60' : ''}`} aria-busy={loading}>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="returns-headline">
              <Stat label="Return rate" value={pct(s.rate)} testId="returns-rate"
                sub={<>{number(s.returned_units)} of {number(s.sold_units)} sold on {(s.rate_platform_labels || []).join(', ') || 'these platforms'}<Change now={s.rate} before={s.previous_rate} points /></>} />
              <Stat label="Customer returns" value={pct(s.customer_rate)} testId="returns-customer-rate"
                sub={<>{number(s.customer_units)} units<Change now={s.customer_rate} before={s.previous_customer_rate} points /></>} />
              <Stat label="Not delivered" value={number(s.undelivered_units)} testId="returns-undelivered"
                sub={<>refused or undeliverable<Change now={s.undelivered_units} before={s.previous_undelivered_units} /></>} />
              <Stat label="Value returned (est.)" value={shortMoney(s.value_est)} sub={`was ${shortMoney(s.previous_value_est)}`} />
              <Stat label="Deducted from sales" value={shortMoney(s.deducted_value)} sub="Amazon Seller API sales" />
              <Stat label="Revenue after returns" value={shortMoney(s.revenue_after_returns)} sub={`of ${shortMoney(s.revenue)} sold`} />
            </div>

            {c && (
              <Section title="Of this period's sales, how many came back" testId="returns-cohort"
                subtitle={c.basis}
                right={c.maturing && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-800 ring-1 ring-inset ring-amber-200">Still maturing</span>}>
                {c.sold_units ? (
                  <p className="text-sm text-gray-800">
                    <span className="text-lg font-semibold tabular-nums">{pct(c.rate)}</span> came back so far
                    ({number(c.returned_units)} of {number(c.sold_units)} units); customer returns <span className="font-semibold">{pct(c.customer_rate)}</span>.
                    {c.maturing && <span className="block text-xs text-gray-500">The period ended {c.days_since_period_end} day(s) ago; most returns arrive within a month, so this will rise.</span>}
                  </p>
                ) : <p className="text-xs text-gray-500">No order-level sales in this scope (only Amazon Seller API sales carry order ids).</p>}
              </Section>
            )}

            <Section title="Why they came back" subtitle="Amazon's reason codes in buckets; Not delivered is a delivery problem, not a product one">
              <Breakdown data={data.reasons} colors={BUCKET_COLOR} testId="returns-reasons" itemsLabel="reason" />
            </Section>

            <Section title="What came back" subtitle="FBA disposition (or the warehouse QC grade): what goes back to stock, what to claim, what is lost">
              <Breakdown data={data.conditions} colors={CONDITION_COLOR} testId="returns-conditions" itemsLabel="condition" />
            </Section>

            <Section title="Where they come from" right={<MetricToggle value={where} onChange={setWhere} options={WHERE} label="Group returns by" />}>
              <WhereTable rows={data[where]} />
            </Section>

            <Section title="Problem SKUs" subtitle="Customer returns only; a SKU opens its product detail"
              right={<MetricToggle value={skuView} onChange={setSkuView} options={SKU_VIEWS} label="Rank SKUs by" />}>
              <ProblemSkus rows={data.problem_skus?.[skuView]} onOpenSku={onOpenSku} view={skuView} />
            </Section>

            {nc && (nc.waiting_for_sku.units > 0 || nc.requested_not_refunded.units > 0) && (
              <Section title="Not counted yet" testId="returns-not-counted">
                <ul className="space-y-1 text-xs text-gray-700">
                  {nc.waiting_for_sku.units > 0 && (
                    <li>{number(nc.waiting_for_sku.units)} unit(s) in {number(nc.waiting_for_sku.rows)} Amazon return row(s) wait for a SKU mapping.{' '}
                      <Link to="/code-mappings?source=returns" className="font-medium text-blue-700 hover:underline">Map them</Link></li>
                  )}
                  {nc.requested_not_refunded.units > 0 && (
                    <li>{number(nc.requested_not_refunded.units)} seller-fulfilled unit(s) were requested for return but not refunded yet.</li>
                  )}
                </ul>
              </Section>
            )}

            <ul className="space-y-0.5 border-t border-gray-200 pt-3 text-[11px] leading-snug text-gray-500">
              {s.all_platform_rate != null && <li>· The Returns card shows {pct(s.all_platform_rate)}: the same returns over every platform's sales ({number(s.all_platform_sold_units)} units).</li>}
              {data.notes.map(note => <li key={note}>· {note}</li>)}
              <li>· <Link to="/returns" className="text-blue-700 hover:underline">Returns records and warehouse QC →</Link></li>
            </ul>
          </div>
        )}
    </>
  );
};

export default ReturnsPanel;
